package com.appsyde.shared.presentation.screen.component.menu

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.presentation.screen.component.SpacerXSmall
import com.appsyde.shared.presentation.screen.component.message.helper.intentLabelFr
import com.appsyde.shared.presentation.screen.component.message.mapper.uiLabelForKey
import com.appsyde.shared.presentation.style.style.AppStylesCards
import com.appsyde.shared.presentation.style.style.AppStylesLeftSidebar
import com.appsyde.shared.presentation.style.style.AppStylesRightSidebar
import com.appsyde.shared.presentation.style.style.AppStylesTicketsSidebar
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.InsightStyles
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.autoFocus
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

fun documentIcon(mimeType: String): String = when {
    mimeType.startsWith("image/") -> "🖼️"
    mimeType == "application/pdf" -> "📄"
    mimeType.startsWith("application/") -> "📋"
    mimeType.startsWith("text/") -> "📝"
    else -> "📎"
}

fun shortTitle(filename: String, maxLen: Int = 28): String =
    if (filename.length <= maxLen) filename else filename.take(maxLen - 3) + "…"

@Composable
fun RightSidebar(
    resolutionTitle: String,
    recommendedAction: String,
    recommendedActionWhy: String = "",
    isRefreshing: Boolean,
    needContext: Boolean,
    /** Clé (ex. ticketId) pour réinitialiser l’état : au changement de ticket, Contexte repasse en replié. */
    contextCollapseKey: String? = null,
    missingInfo: List<String>,
    toConfirmKeys: List<String> = emptyList(),
    context: String?,
    onAddMemory: (key: String, value: String) -> Unit,
    onConfirmKey: (key: String) -> Unit,
) {
    Style(InsightStyles)

    val actionText = recommendedAction
        .trim()
        .ifBlank { "—" }
        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }

    val missingRows: List<Triple<String, String, String>> =
        missingInfo
            .asSequence()
            .mapNotNull { rawKey ->
                val key = rawKey.trim().lowercase()
                if (key.isBlank()) return@mapNotNull null
                val label = uiLabelForKey(key) ?: key.replace("_", " ").replaceFirstChar { it.uppercase() }
                Triple("❌", label, key)
            }
            .distinctBy { it.second }
            .toList()

    val toConfirmRows: List<Triple<String, String, String>> =
        toConfirmKeys
            .asSequence()
            .mapNotNull { rawKey ->
                val key = rawKey.trim().lowercase()
                if (key.isBlank()) return@mapNotNull null
                val label = uiLabelForKey(key) ?: key.replace("_", " ").replaceFirstChar { it.uppercase() }
                Triple("?", label, key)
            }
            .distinctBy { it.second }
            .toList()

    Div(attrs = { classes(AppStylesRightSidebar.ticketSidebar, InsightStyles.sidebarRoot) }) {

        // ============================================
        // CONTEXTE — card collapsible : par défaut replié, replié au changement de ticket
        // ============================================
        if (needContext) {
            var isContextExpanded by remember { mutableStateOf(false) }
            LaunchedEffect(contextCollapseKey) {
                isContextExpanded = false
            }

            Div(attrs = {
                classes(
                    listOfNotNull(
                        AppStylesCards.ticketCardGlass,
                        InsightStyles.panelCard,
                        if (!isContextExpanded) InsightStyles.contextCardCollapsed else null
                    )
                )
            }) {

                Div(attrs = {
                    classes(
                        listOfNotNull(
                            InsightStyles.panelHeaderCollapsible,
                            if (isContextExpanded) InsightStyles.panelHeaderCollapsibleExpanded else InsightStyles.panelHeaderCollapsibleCollapsed
                        )
                    )
                    onClick { isContextExpanded = !isContextExpanded }
                }) {
                    Div(attrs = { classes(InsightStyles.panelTitleWrap) }) {
                        Span(attrs = { classes(AppStylesLeftSidebar.sidebarCardKicker) }) { Text("Ticket") }
                        Span(attrs = { classes(InsightStyles.panelTitle) }) { Text("Contexte") }
                    }

                    Span(attrs = { classes(InsightStyles.panelHeaderRight) }) {
                        Span(
                        attrs = {
                            classes(
                                listOfNotNull(
                                    InsightStyles.statusPill,
                                    if (isRefreshing) InsightStyles.statusPillMid else InsightStyles.statusPillOk
                                )
                            )
                        }
                    ) {
                        Span(
                            attrs = {
                                classes(
                                    listOfNotNull(
                                        InsightStyles.statusDot,
                                        if (isRefreshing) InsightStyles.statusDotProcessing else InsightStyles.statusDotOk
                                    )
                                )
                            }
                        )
                        Text(if (isRefreshing) "Analyse en cours" else "Analysé")
                    }
                    Span(attrs = { classes(InsightStyles.panelHeaderChevron) }) {
                        Text(if (isContextExpanded) "▼" else "▲")
                    }
                    }
                }

                Div(attrs = {
                    classes(
                        InsightStyles.contextBodyWrap,
                        if (isContextExpanded) InsightStyles.contextBodyExpanded else InsightStyles.contextBodyCollapsed
                    )
                }) {
                    Div(attrs = { classes(InsightStyles.contextBodyInner) }) {
                        Div(attrs = {
                            classes(listOfNotNull(InsightStyles.summaryWrap))
                        }) {
                            Div(attrs = { classes(if (context.isNullOrBlank()) InsightStyles.summaryTextEmpty else InsightStyles.summaryText) }) {
                                Text(context?.takeIf { it.isNotBlank() } ?: "Aucun résumé pour l'instant")
                            }
                        }
                    }
                }
            }
        }

        // ============================================
        // COPILOTE — Poste de commandement (card autour, structure verticale)
        // Ordre : 1) Prochaine action  2) À collecter  3) À confirmer
        // ============================================
        Div(attrs = { classes(AppStylesCards.ticketCardGlass, InsightStyles.panelCard) }) {
        Div(attrs = { classes(InsightStyles.copilotRoot) }) {

            // ---------- 1. PROCHAINE ACTION (seul bloc avec fond léger violet) ----------
            Span(attrs = { classes(InsightStyles.copilotResolutionTitle) }) {
                Text(resolutionTitle)
            }
            Div(attrs = {
                classes(
                    listOfNotNull(
                        InsightStyles.copilotDirectionBlock,
                        if (isRefreshing) AppStylesRightSidebar.sidebarContentDimmed else null
                    )
                )
            }) {
                Span(attrs = { classes(InsightStyles.copilotStepLabel) }) { Text("Prochaine étape") }
                key(actionText + recommendedActionWhy) {
                    Div(attrs = { classes(AppStylesAnimations.copilotStepActionReveal) }) {
                        Div(attrs = {
                            classes(
                                if (actionText == "—") InsightStyles.actionTextEmpty else InsightStyles.copilotStepAction
                            )
                        }) {
                            Text(if (actionText == "—") "Aucune action suggérée" else actionText)
                        }
                        if (recommendedActionWhy.isNotBlank()) {
                            Div(attrs = { classes(InsightStyles.copilotStepWhy) }) {
                                Text(recommendedActionWhy)
                            }
                        }
                    }
                }
            }

            var editingItem by remember { mutableStateOf<String?>(null) }
            var editingValue by remember { mutableStateOf("") }
            var hoveredConfirm by remember { mutableStateOf<String?>(null) }
            var hoveredMissing by remember { mutableStateOf<String?>(null) }

            // ---------- 2. À COLLECTER : [+] visible au hover de la ligne, clic sur toute la ligne ouvre l'input ----------
            if (!missingRows.isNullOrEmpty()) {
                Div(attrs = { classes(InsightStyles.copilotSectionDivider) })
                Span(attrs = { classes(InsightStyles.copilotSectionTitleCollect) }) {
                    Text("À collecter")
                }

                Div(attrs = { classes(InsightStyles.pointsListSimple) }) {
                    missingRows.forEach { (_, label, key) ->
                        val itemKey = "missing:$key"
                        val isEditing = editingItem == itemKey
                        val isRowHovered = hoveredMissing == itemKey

                        Div(attrs = {
                            classes(
                                *listOf(InsightStyles.copilotListItem, InsightStyles.copilotListItemMissing)
                                    .plus(if (isEditing) listOf(InsightStyles.copilotListItemEditing) else listOf(InsightStyles.copilotListItemClickable))
                                    .toTypedArray()
                            )
                            onMouseEnter { hoveredMissing = itemKey }
                            onMouseLeave { hoveredMissing = null }
                            onClick {
                                if (!isEditing) {
                                    editingItem = itemKey
                                    editingValue = ""
                                }
                            }
                        }) {
                            when {
                                isEditing -> {
                                    Div(attrs = { classes(InsightStyles.pointItemEditRow) }) {
                                        Input(InputType.Text) {
                                            classes(InsightStyles.pointInputInline)
                                            value(editingValue)
                                            onInput { editingValue = (it.target as? HTMLInputElement)?.value.orEmpty() }
                                            onKeyDown {
                                                if (it.key == "Enter" && editingValue.isNotBlank()) {
                                                    onAddMemory(key, editingValue.trim())
                                                    editingItem = null
                                                    editingValue = ""
                                                } else if (it.key == "Escape") {
                                                    editingItem = null
                                                    editingValue = ""
                                                }
                                            }
                                            attr("placeholder", label)
                                            autoFocus()
                                        }
                                        Button(attrs = {
                                            classes(InsightStyles.copilotListAction, InsightStyles.copilotListActionConfirm)
                                            onClick {
                                                if (editingValue.isNotBlank()) {
                                                    onAddMemory(key, editingValue.trim())
                                                    editingItem = null
                                                    editingValue = ""
                                                }
                                            }
                                        }) { Text("✓") }
                                        Button(attrs = {
                                            classes(InsightStyles.copilotListAction)
                                            onClick {
                                                editingItem = null
                                                editingValue = ""
                                            }
                                        }) { Text("✕") }
                                    }
                                }
                                else -> {
                                    Span(attrs = { classes(InsightStyles.copilotListItemLabelMissing) }) { Text(label) }
                                    Button(attrs = {
                                        classes(InsightStyles.copilotListActionFaintMissing)
                                        onClick { it.stopPropagation(); editingItem = itemKey; editingValue = "" }
                                    }) { Text("+") }
                                }
                            }
                        }
                    }
                }
            }

            // ---------- 3. À CONFIRMER : "Valider" ghost discret, icône ✓ au hover ----------
            if (toConfirmRows.isNotEmpty()) {
                Div(attrs = { classes(InsightStyles.copilotSectionDivider) })
                Span(attrs = { classes(InsightStyles.copilotSectionTitleConfirm) }) {
                    Text("À confirmer")
                }

                Div(attrs = { classes(InsightStyles.pointsListSimple) }) {
                    toConfirmRows.forEach { (_, label, key) ->
                        val itemKey = "confirm:$key"
                        val isHovered = hoveredConfirm == itemKey
                        Div(attrs = {
                            classes(InsightStyles.copilotListItem)
                            onMouseEnter { hoveredConfirm = itemKey }
                            onMouseLeave { hoveredConfirm = null }
                        }) {
                            Span(attrs = { classes(InsightStyles.copilotListItemLabel) }) { Text(label) }
                            Button(attrs = {
                                classes(InsightStyles.copilotListActionValiderGhost)
                                onClick { onConfirmKey(key) }
                            }) {
                                if (isHovered) Span(attrs = { classes(InsightStyles.copilotListActionIcon) }) { Text("✓ ") }
                                Text("Valider")
                            }
                        }
                    }
                }
            }
        }
        }
    }
}
